import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  ArrowRight,
  FlaskConical,
  ShieldCheck,
  Leaf,
  Ban,
  Check,
  Star,
  Flower2,
  Sparkles,
  Sunrise,
  Droplets,
  MoonStar,
} from "lucide-react";
import { Reveal } from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";
import {
  BRAND,
  IMAGES,
  TRUST_BADGES,
  CATEGORIES,
  SCIENCE_POINTS,
  PREMIUM_UPGRADES,
  TESTIMONIALS,
} from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = { FlaskConical, ShieldCheck, Leaf, Ban };

/* ---------- Hero ---------- */
const Hero = () => {
  const [slides, setSlides] = useState([]);
  const [idx, setIdx] = useState(0);

  // Build the slideshow from every product's gallery (main photo first), de-duplicated.
  useEffect(() => {
    axios
      .get(`${API}/products`)
      .then((r) => {
        const seen = new Set();
        const list = [];
        r.data.forEach((p) => {
          const urls = (p.gallery && p.gallery.length ? p.gallery : [p.photo]).filter(Boolean);
          urls.forEach((u) => {
            if (!seen.has(u)) {
              seen.add(u);
              list.push({ src: u, name: p.name });
            }
          });
        });
        setSlides(list);
      })
      .catch(() => setSlides([]));
  }, []);

  useEffect(() => {
    if (slides.length < 2) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), 2600);
    return () => clearInterval(t);
  }, [slides.length]);

  const current = slides[idx];

  return (
  <section data-testid="hero-section" className="pt-[110px]" style={{ background: "linear-gradient(160deg,#FBF1F3 0%,#FDFBF7 45%,#F3EEF8 100%)" }}>
    <div className="max-w-7xl mx-auto px-5 md:px-10 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
      <div>
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-rose bg-rose/5 border border-gold/40 rounded-full px-3 py-1.5"
        >
          Clinically-formulated · For women, globally
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.05 }}
          className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl font-medium tracking-tight text-ink leading-[1.02]"
        >
          Science-backed wellness for every season of a woman's life.
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.12 }}
          className="mt-6 text-lg text-gray-600 max-w-xl leading-relaxed"
        >
          Thirteen considered formulas built on bioavailable, active-form nutrients — with fully
          transparent labels and nothing she doesn't need.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.18 }}
          className="mt-9 flex flex-wrap gap-3"
        >
          <Link
            to="/products"
            data-testid="hero-shop-cta"
            className="inline-flex items-center gap-2 rounded-full bg-ink text-white font-semibold px-7 py-3.5 hover:bg-rose transition-colors duration-200"
          >
            Shop all products <ArrowRight size={17} />
          </Link>
          <a
            href="#science"
            data-testid="hero-science-cta"
            className="inline-flex items-center rounded-md bg-white text-ink border border-gray-300 font-semibold px-7 py-3.5 hover:border-ink transition-colors duration-200"
          >
            The science
          </a>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.7, delay: 0.1 }}
        data-testid="hero-slideshow"
        className="relative rounded-xl overflow-hidden border border-gray-200 bg-gray-50 aspect-[4/3] lg:aspect-[5/4]"
      >
        {slides.length === 0 ? (
          <img src={BRAND.heroCollage} alt="Femora Life product collection" className="w-full h-full object-cover" />
        ) : (
          slides.map((s, i) => (
            <img
              key={s.src}
              src={s.src}
              alt={i === idx ? `${s.name} by Femora Life` : ""}
              aria-hidden={i !== idx}
              data-testid={i === idx ? "hero-slide-active" : undefined}
              className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
              style={{ opacity: i === idx ? 1 : 0 }}
            />
          ))
        )}
        {current && (
          <span
            data-testid="hero-slide-label"
            className="absolute bottom-4 left-4 rounded-full bg-white/85 backdrop-blur-sm border border-gray-200 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-widest text-ink"
          >
            {current.name}
          </span>
        )}
      </motion.div>
    </div>
  </section>
  );
};

/* ---------- Trust bar ---------- */
const TRUST_COLORS = ["#B76E79", "#8E7CC3", "#5B9A8B", "#E0913E"];
const TrustBar = () => (
  <section data-testid="trust-bar" className="bg-white border-y border-gray-200">
    <div className="max-w-7xl mx-auto px-5 md:px-10 py-7 grid grid-cols-2 lg:grid-cols-4 gap-5">
      {TRUST_BADGES.map((b, i) => {
        const Icon = ICONS[b.icon];
        const c = TRUST_COLORS[i % TRUST_COLORS.length];
        return (
          <div key={b.label} className="flex items-center gap-3">
            <span className="shrink-0 h-11 w-11 rounded-full flex items-center justify-center" style={{ backgroundColor: `${c}1A` }}>
              <Icon size={20} style={{ color: c }} strokeWidth={1.8} />
            </span>
            <span className="text-sm font-semibold text-ink leading-tight">{b.label}</span>
          </div>
        );
      })}
    </div>
  </section>
);

/* ---------- Categories ---------- */
const Categories = () => (
  <section className="bg-white py-16 lg:py-24">
    <div className="max-w-7xl mx-auto px-5 md:px-10">
      <Reveal className="mb-10 max-w-2xl">
        <p className="text-xs font-semibold uppercase tracking-widest text-rose mb-3 inline-block border-l-2 border-gold pl-3">Shop by need</p>
        <h2 className="font-display text-4xl lg:text-5xl font-medium tracking-tight text-ink">Find your formula</h2>
      </Reveal>
      <div className="grid md:grid-cols-2 gap-6">
        {CATEGORIES.map((c, i) => (
          <Reveal key={c.key} delay={i * 0.08}>
            <Link
              to={`/products?collection=${c.key}`}
              data-testid={`category-${c.key}`}
              className="group block relative rounded-xl overflow-hidden border border-gray-200 aspect-[16/10]"
            >
              <img src={c.image} alt={c.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-white">
                <h3 className="text-xl md:text-2xl font-bold">{c.title}</h3>
                <p className="text-sm text-white/80 mt-2 max-w-md">{c.desc}</p>
                <span className="inline-flex items-center gap-1.5 mt-4 text-sm font-semibold">
                  Explore <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform duration-200" />
                </span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- Vegan strip ---------- */
const VeganStrip = () => (
  <section data-testid="vegan-strip" className="bg-emerald-950 text-white">
    <div className="max-w-7xl mx-auto px-5 md:px-10 py-14 lg:py-20 grid lg:grid-cols-2 gap-10 items-center">
      <Reveal>
        <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-emerald-300 bg-emerald-300/10 border border-emerald-300/30 rounded-full px-3 py-1.5">
          <Leaf size={13} /> Plant-powered promise
        </p>
        <h2 className="mt-5 font-display text-4xl lg:text-5xl font-medium tracking-tight">
          100% vegan sourcing. Zero compromise.
        </h2>
        <p className="mt-4 text-base text-white/70 max-w-lg leading-relaxed">
          Ten of our formulas are now fully vegan — every nutrient drawn from plants, red algae,
          lichen or clean fermentation. Our D3 comes from lichen, never sheep's wool. Our omega-3
          from algae, never fish. Our calcium grows on the ocean floor, not in a quarry.
        </p>
        <Link
          to="/product/postpartum"
          data-testid="vegan-strip-cta"
          className="mt-7 inline-flex items-center gap-2 rounded-full bg-emerald-300 text-emerald-950 font-semibold px-7 py-3.5 hover:bg-white transition-colors duration-200"
        >
          See the vegan Postpartum formula <ArrowRight size={16} />
        </Link>
      </Reveal>
      <Reveal delay={0.1}>
        <div className="grid grid-cols-2 gap-4">
          {[
            { title: "Red algae", desc: "Algal Calcium — plant-grown, not mined limestone" },
            { title: "Lichen", desc: "The only fully plant-based source of Vitamin D3" },
            { title: "Sunflower", desc: "Natural Vitamin E tocopherols, gently pressed" },
            { title: "Fermentation", desc: "Active B12, folate & ubiquinol — no animal input" },
          ].map((s, i) => (
            <div key={i} data-testid={`vegan-strip-card-${i}`} className="rounded-xl border border-white/15 bg-white/5 p-5">
              <span className="inline-flex h-9 w-9 rounded-full bg-emerald-300/15 items-center justify-center">
                <Leaf size={16} className="text-emerald-300" />
              </span>
              <p className="mt-3 font-semibold">{s.title}</p>
              <p className="mt-1 text-sm text-white/60 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---------- Featured ---------- */
const Featured = () => {
  const [products, setProducts] = useState([]);
  useEffect(() => {
    axios.get(`${API}/products`).then((r) => setProducts(r.data)).catch(() => setProducts([]));
  }, []);

  return (
    <section className="border-y border-gray-200 py-16 lg:py-24" style={{ background: "linear-gradient(180deg,#F1F6F3 0%,#FBF1F3 100%)" }}>
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <Reveal className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-rose mb-3 inline-block border-l-2 border-gold pl-3">The collection</p>
            <h2 className="font-display text-4xl lg:text-5xl font-medium tracking-tight text-ink">Bestselling formulas</h2>
          </div>
          <Link to="/products" data-testid="view-all-products" className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink hover:text-rose transition-colors duration-200">
            View all 13 products <ArrowRight size={15} />
          </Link>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {products.slice(0, 8).map((p, i) => (
            <ProductCard key={p.slug} product={p} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- Science ---------- */
const Science = () => (
  <section id="science" data-testid="science-section" className="bg-ink text-white py-16 lg:py-24">
    <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
      <Reveal>
        <div className="rounded-xl overflow-hidden border border-white/10 aspect-[4/3]">
          <img src={IMAGES.science} alt="Clinical research" className="w-full h-full object-cover" />
        </div>
      </Reveal>
      <div>
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-widest text-rose mb-3 inline-block border-l-2 border-gold pl-3">The science</p>
          <h2 className="font-display text-4xl lg:text-5xl font-medium tracking-tight mb-4">Not what's on the label. What your body absorbs.</h2>
          <p className="text-white/60 mb-8 max-w-lg">
            Most supplements use the cheapest raw materials available. We start from the opposite question — which form does the body actually use — and build from there.
          </p>
        </Reveal>
        <div className="space-y-6">
          {SCIENCE_POINTS.map((s, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <div className="flex gap-4">
                <span className="shrink-0 h-7 w-7 rounded-full bg-white/10 flex items-center justify-center">
                  <Check size={15} className="text-rose" />
                </span>
                <div>
                  <h3 className="font-semibold">{s.title}</h3>
                  <p className="text-sm text-white/55 mt-1 max-w-lg">{s.body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ---------- Bioavailability upgrades (About) ---------- */
const Bioavailability = () => (
  <section id="about" className="bg-white py-16 lg:py-24">
    <div className="max-w-5xl mx-auto px-5 md:px-10">
      <Reveal className="mb-10 max-w-2xl">
        <p className="text-xs font-semibold uppercase tracking-widest text-rose mb-3 inline-block border-l-2 border-gold pl-3">The bioavailability difference</p>
        <h2 className="font-display text-4xl lg:text-5xl font-medium tracking-tight text-ink">We upgrade every ingredient form</h2>
      </Reveal>
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="grid grid-cols-12 bg-gray-50 border-b border-gray-200 px-5 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
          <div className="col-span-4 md:col-span-3">Standard form</div>
          <div className="col-span-8 md:col-span-9">Femora Life uses</div>
        </div>
        {PREMIUM_UPGRADES.map((u, i) => (
          <Reveal key={i}>
            <div className="grid grid-cols-12 items-start gap-2 px-5 py-4 border-b border-gray-100 last:border-b-0">
              <div className="col-span-4 md:col-span-3 text-sm text-gray-400 line-through">{u.from}</div>
              <div className="col-span-8 md:col-span-9">
                <p className="text-sm font-semibold text-ink">{u.to}</p>
                <p className="text-sm text-gray-500 mt-0.5">{u.why}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const TINTS = ["#B76E79", "#8E7CC3", "#5B9A8B", "#3F5488", "#D46A82"];
const Testimonials = () => (
  <section data-testid="testimonials-section" className="py-16 lg:py-24" style={{ background: "linear-gradient(180deg,#FBF1F3 0%,#F3EEF8 100%)" }}>
    <div className="max-w-7xl mx-auto px-5 md:px-10">
      <Reveal className="text-center max-w-2xl mx-auto mb-12">
        <p className="text-xs font-semibold uppercase tracking-widest text-rose mb-3 inline-block border-l-2 border-gold pl-3">Chosen · Trusted · Loved</p>
        <h2 className="font-display text-4xl lg:text-5xl font-medium tracking-tight text-ink">Loved by women, everywhere</h2>
        <div className="flex items-center justify-center gap-1.5 mt-4">
          {[...Array(5)].map((_, i) => (<Star key={i} size={20} className="fill-gold text-gold" />))}
          <span className="ml-2 text-sm font-semibold text-ink">4.9 average from early reviewers</span>
        </div>
      </Reveal>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {TESTIMONIALS.map((t, i) => (
          <Reveal key={i} delay={(i % 3) * 0.06}>
            <div className="h-full bg-white rounded-2xl border border-white p-6 shadow-[0_10px_40px_-20px_rgba(10,17,40,0.25)]">
              <div className="flex items-center gap-1 mb-3">
                {[...Array(t.rating)].map((_, s) => (<Star key={s} size={15} className="fill-gold text-gold" />))}
              </div>
              <p className="text-ink leading-relaxed">"{t.text}"</p>
              <div className="flex items-center gap-3 mt-5 pt-5 border-t border-gray-100">
                <span className="h-10 w-10 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: TINTS[i % TINTS.length] }}>
                  {t.name.charAt(0)}
                </span>
                <div>
                  <p className="text-sm font-semibold text-ink">{t.name} · <span className="text-gray-400 font-normal">{t.location}</span></p>
                  <p className="text-xs text-rose font-medium">Femora Life {t.product}</p>
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- Founder Story ---------- */
const FounderStory = () => (
  <section
    id="story"
    data-testid="founder-story-section"
    className="py-20 lg:py-28"
    style={{ background: "linear-gradient(180deg,#FDFBF7 0%,#F7F0E9 100%)" }}
  >
    <div className="max-w-3xl mx-auto px-5 md:px-10">
      <Reveal>
        <p className="text-xs font-semibold uppercase tracking-widest text-rose mb-4 inline-block border-l-2 border-gold pl-3">
          The story behind
        </p>
      </Reveal>
      <Reveal delay={0.05}>
        <h2 className="font-display text-4xl lg:text-5xl font-medium tracking-tight text-ink leading-[1.08]">
          Why we built <span className="italic text-rose">Femora Life</span>
        </h2>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="relative mt-10 md:pl-8">
          <span
            className="hidden md:block absolute left-0 top-2 font-display italic text-[6rem] leading-none text-gold/40 select-none"
            aria-hidden="true"
          >
            "
          </span>
          <p className="font-display text-lg md:text-xl text-ink/85 leading-[1.75] italic">
            When someone I love was quietly told her PCOS could "wait," I started reading
            supplement labels — and what I found broke me. Tiny doses. Proprietary blends.
            Generic multivitamins simply repackaged for women.
          </p>
          <p className="font-display text-lg md:text-xl text-ink/85 leading-[1.75] italic mt-5">
            Femora Life is the answer I wished existed then. Thirteen honest formulas.
            Active-form nutrients at therapeutic doses. Nothing she doesn't need — made
            with the care she deserves.
          </p>
        </div>
      </Reveal>

      <Reveal delay={0.15}>
        <div className="mt-10 md:pl-8 flex items-end gap-5">
          <div className="flex-1">
            <p
              className="text-[2.75rem] md:text-[3.25rem] leading-none text-ink"
              style={{ fontFamily: "'Homemade Apple', 'Dancing Script', cursive" }}
              data-testid="founder-signature"
            >
              Ravali
            </p>
            <div className="mt-3 flex items-center gap-3">
              <span className="block h-px w-10 bg-gold" aria-hidden="true" />
              <p className="text-sm text-ink">
                <span className="font-semibold">Ravali Vuppula</span>
                <span className="text-gray-500"> · Founder, Femora Life</span>
              </p>
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---------- Featured concerns strip ---------- */
const CONCERNS = [
  { slug: "fertility", label: "Fertility", tag: "Trying to conceive", Icon: Flower2, color: "#B76E79", bg: "#FBF1F3" },
  { slug: "pcos", label: "PCOS", tag: "Cycle & metabolic support", Icon: Sparkles, color: "#8E7CC3", bg: "#F3EEF8", badge: "100% Indian RDA" },
  { slug: "perimenopause", label: "Perimenopause", tag: "Regulate by day, calm by night", Icon: MoonStar, color: "#8A4B6E", bg: "#F7EBF3", badge: "AM + PM protocol" },
  { slug: "menopause", label: "Menopause", tag: "For calm & clarity", Icon: Sunrise, color: "#E0913E", bg: "#FBF4EA", badge: "AM + PM protocol" },
  { slug: "uti", label: "Urinary Balance", tag: "D-Mannose cranberry mix", Icon: Droplets, color: "#B34450", bg: "#FBE9EC" },
];

const FeaturedConcerns = () => (
  <section
    data-testid="featured-concerns"
    className="bg-white border-b border-gray-200 py-10 lg:py-12"
  >
    <div className="max-w-7xl mx-auto px-5 md:px-10">
      <Reveal className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-rose mb-2 inline-block border-l-2 border-gold pl-3">
            One-tap to your formula
          </p>
          <h2 className="font-display text-2xl md:text-3xl font-medium tracking-tight text-ink">
            Shop by concern
          </h2>
        </div>
        <Link
          to="/products"
          data-testid="concerns-view-all"
          className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-ink hover:text-rose transition-colors duration-200"
        >
          See all thirteen <ArrowRight size={15} />
        </Link>
      </Reveal>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
        {CONCERNS.map((c, i) => (
          <Reveal key={c.slug} delay={i * 0.05}>
            <Link
              to={`/product/${c.slug}`}
              data-testid={`concern-${c.slug}`}
              className="group flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 md:p-5 hover:border-ink hover:shadow-[0_10px_30px_-16px_rgba(10,17,40,0.25)] transition-all duration-200"
            >
              <span
                className="shrink-0 h-12 w-12 md:h-14 md:w-14 rounded-full flex items-center justify-center"
                style={{ backgroundColor: c.bg }}
                aria-hidden="true"
              >
                <c.Icon size={22} style={{ color: c.color }} strokeWidth={1.7} />
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-display text-lg text-ink leading-none">{c.label}</p>
                  {c.badge && (
                    <span
                      data-testid={`concern-${c.slug}-badge`}
                      className="hidden md:inline-block text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-full border"
                      style={{ color: c.color, borderColor: `${c.color}55`, backgroundColor: c.bg }}
                    >
                      {c.badge}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1 truncate">{c.tag}</p>
                {c.badge && (
                  <span
                    className="md:hidden inline-block mt-1 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-full border"
                    style={{ color: c.color, borderColor: `${c.color}55`, backgroundColor: c.bg }}
                  >
                    {c.badge}
                  </span>
                )}
              </div>
              <ArrowRight size={16} className="hidden md:block text-gray-300 group-hover:text-ink group-hover:translate-x-0.5 transition-all duration-200" />
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Home = () => (
  <main data-testid="home-page">
    <Hero />
    <TrustBar />
    <VeganStrip />
    <FeaturedConcerns />
    <Categories />
    <Featured />
    <FounderStory />
    <Science />
    <Testimonials />
    <Bioavailability />
  </main>
);

export default Home;
